import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListOrganizationsInput, OrgKind, OrgType } from "@nexxt/contracts/party";
import { partyQueries } from "@nexxt/party-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the directory (50-api/party.md §2.3): the organizations by page, one with its business roles, its
// history, the customers that look like duplicates, and what the forms offer. The same list, searched by name, is
// what every picker reads.

const organizations = api.party.organizations;
const byId = organizations[":organizationId"];

type ListPage = InferResponseType<typeof organizations.$get, 200>;
export type OrganizationSummary = ListPage["items"][number];
export type Organization = InferResponseType<typeof byId.$get, 200>;
export type OrganizationOptions = InferResponseType<(typeof api.party)["organization-options"]["$get"], 200>;
const recipients = api.party["order-confirmation-recipients"];
type DuplicatePage = InferResponseType<(typeof api.party)["organization-duplicates"]["$get"], 200>;
export type DuplicateGroup = DuplicatePage["items"][number];

/** The filters of the list input (`listOrganizationsInput`), without the paging the query adds. */
export type OrganizationFilters = Pick<ListOrganizationsInput, "q" | "kind" | "bcSyncStatus"> & {
  withoutOrderContact?: true | undefined;
  toRegisterOnBc?: true | undefined;
  archived?: boolean | undefined;
  /** The sets of the queues "To do", as `Todo` writes them (ADR-302). */
  anyOf?: string | undefined;
};

/** What a picker narrows its search to: Tomorrow or the others, and what the organization is to Tomorrow. */
export interface OrganizationScope {
  type?: OrgType | undefined;
  kind?: OrgKind | undefined;
}

/** The tabs of the page of an organization, in the URL (`?tab=`). */
export const organizationTabs = ["details", "activity", "addresses", "contacts", "bc", "history"] as const;
export type OrganizationTab = (typeof organizationTabs)[number];

const PAGE_SIZE = 50;
/** As many as a search shows: a longer answer asks for a longer name. */
const FOUND = 20;

const keys = {
  all: [...partyQueries.all, "organizations"] as const,
  lists: [...partyQueries.all, "organizations", "list"] as const,
  details: partyQueries.organizationDetails,
};

export const organizationQueries = {
  ...keys,

  list: (filters: OrganizationFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          organizations.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  detail: (organizationId: string) =>
    queryOptions({
      queryKey: [...keys.details, organizationId],
      queryFn: ({ signal }) => unwrap(byId.$get({ param: { organizationId } }, { init: { signal } })),
    }),

  history: (organizationId: string) =>
    infiniteQueryOptions({
      queryKey: [...keys.details, organizationId, "history"],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          byId.history.$get(
            { param: { organizationId }, query: pageParam ? { cursor: pageParam } : {} },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  /** The ones that can be chosen with `q` in the name, the legal name, the VAT number or a code. */
  search: (q: string, scope: OrganizationScope = {}) =>
    queryOptions({
      queryKey: [...keys.all, "search", q, scope.type ?? null, scope.kind ?? null],
      queryFn: async ({ signal }) =>
        (
          await unwrap(
            organizations.$get(
              {
                query: { ...filterQuery({ q, ...scope }), pageSize: String(FOUND) },
              },
              { init: { signal } },
            ),
          )
        ).items,
    }),

  /** Customers that look like the same company, as groups: a proposal (ADR-101, point 9). */
  duplicates: () =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "duplicates"],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          api.party["organization-duplicates"].$get(
            { query: { page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** Who reads the order confirmations of a brand in copy (ADR-202), under the brand: a write reloads them. */
  recipients: (brandOrgId: string) =>
    queryOptions({
      queryKey: [...keys.details, brandOrgId, "order-confirmation-recipients"],
      queryFn: ({ signal }) => unwrap(recipients.$get({ query: { brandOrgId } }, { init: { signal } })),
    }),

  /** The reference data the forms of the directory name by code (ADR-048). */
  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => unwrap(api.party["organization-options"].$get({}, { init: { signal } })),
      staleTime: 10 * 60_000,
    }),
};
